import { useEnvironmentInterface } from "environment-interface";
import type { TaskListResult } from "features/types/task";
import { useLegacyPitcher } from "features/utils/usePitcher";
import { useEffect, useState } from "react";

type State =
  | {
      state: "TASK_STARTING";
    }
  | {
      state: "TASK_STOPPED";
      // It might already be stopped, so we do not have the url then
      src?: string;
    }
  | {
      state: "TASK_RUNNING";
      src: string;
    }
  | {
      state: "ERROR";
      error: string;
    };

export const useTask = (taskName: string, redirect: boolean) => {
  const { navigation } = useEnvironmentInterface();
  const pitcher = useLegacyPitcher();

  const getState = (tasks: TaskListResult["tasks"]): State => {
    const task = tasks[taskName];

    if (!task) {
      return {
        state: "ERROR",
        error: `Task "${taskName}" is not found for this instance`,
      };
    }

    // We only support opening the first port, which is the common one
    const port = task.ports[0];

    if (port) {
      return {
        state: "TASK_RUNNING",
        src: `${navigation.getProtocol()}//${port.url}/`,
      };
    } else {
      pitcher.task.runTask(task.id);
      return {
        state: "TASK_STARTING",
      };
    }
  };

  // We get here by firing up our connection to Pitcher, which leads
  // to confirming already running task or starting it, both considered
  // part of starting it
  const [state, setState] = useState<State>(
    getState(pitcher.task.getTasks().tasks),
  );

  useEffect(
    () =>
      pitcher.events.subscribe((event) => {
        if (event.type === "PITCHER:DISCONNECTED") {
          setState((current) => ({
            state: "TASK_STOPPED",
            src: "src" in current ? current.src : undefined,
          }));
          return;
        }

        if (event.type === "PITCHER:CONNECTED") {
          setState(getState(event.tasks));
        }

        if (
          event.type === "PITCHER:TASK:UPDATED" &&
          event.task.id === taskName
        ) {
          const isStopped =
            !event.task.shell || event.task.shell.status === "KILLED";
          setState((current) =>
            isStopped
              ? {
                  state: "TASK_STOPPED",
                  src: "src" in current ? current.src : undefined,
                }
              : event.task.ports.length
                ? {
                    state: "TASK_RUNNING",
                    src: `${navigation.getProtocol()}//${
                      event.task.ports[0].url
                    }/`,
                  }
                : {
                    state: "TASK_STARTING",
                  },
          );
        }
      }),
    [],
  );

  useEffect(() => {
    if (redirect && state.state === "TASK_RUNNING") {
      navigation.changeUrl(state.src);
    }
  }, [redirect, state, navigation]);

  return state;
};
